import { queryOptions } from "react-query";
import { v4 as uuidv4 } from "uuid";

import { CheckoutError } from "@/react/components/Paywalls/Checkout/CheckoutError";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { paywallCheckout } from "@/react/helpers/urlHelpers/internalApi/paywalls/checkout";
import { createQueryKeyFactory } from "@/react/lib/queries/createQueryKeyFactory";
import type { UpgradePreview } from "@/react/types/Paywalls/Checkout/UpgradePreview";

interface UpgradePricePreviewParams {
  checkoutSessionId: number;
  communityId: number;
  couponMode: UpgradePreview["coupon_mode"];
  paywallId: number;
  priceId: number;
  stepId: number;
}

const upgradePricePreviewKey = createQueryKeyFactory("upgrade-price-preview");

export const upgradePricePreviewQueries = {
  detail: ({
    communityId,
    checkoutSessionId,
    paywallId,
    stepId,
    priceId,
    couponMode,
  }: UpgradePricePreviewParams) =>
    queryOptions({
      queryKey: upgradePricePreviewKey.key(
        "detail",
        communityId,
        checkoutSessionId,
        paywallId,
        stepId,
        priceId,
        couponMode,
      ),
      queryFn: async () => {
        const requestId = uuidv4();
        const response = await reactQueryGet<UpgradePreview>(
          paywallCheckout.sessionStep.upgradePreview({
            paywallId,
            stepId,
            selectedPaywallPriceId: priceId,
            couponMode,
            requestId,
          }),
        );
        if (
          response.request_id !== requestId ||
          response.session_step_id !== stepId ||
          response.coupon_mode !== couponMode ||
          response.selected_paywall_price_id !== priceId ||
          response.destination.paywall_price_id !== priceId ||
          !response.eligible_paywall_price_ids.includes(priceId)
        ) {
          throw CheckoutError.generic("useUpgradePricePreviews");
        }
        const option = response.price_options.find(
          option => option.paywall_price_id === priceId,
        );
        if (
          !option ||
          option.list_amount.amount !== response.destination.list_amount.amount
        ) {
          throw CheckoutError.generic("useUpgradePricePreviews");
        }
        return option;
      },
    }),
};
